import React, { useEffect, useState, useRef } from 'react';
import { FaTimes } from 'react-icons/fa';
import PropTypes from 'prop-types';
import Sidebar from './Sidebar';
import TopNavbar from '../top-navbar/TopNav';
import '../../styles/employee/address-info.css';
import { toast } from 'react-toastify';
import { getAddressInfo, saveAddress } from '../../services/employee/api';

  const AddressInfo = ({ candidateId, addressInfoResponse, onBack }) => {
  const token = localStorage.getItem('bgc_token');

  const [currentAddress, setCurrentAddress] = useState({
    line1: '',
    line2: '',
    country: '',
    state: '',
    city: '',
    pincode: '',
    proof: null,
  });

  const [permanentAddress, setPermanentAddress] = useState({
    line1: '',
    line2: '',
    country: '',
    state: '',
    city: '',
    pincode: '',
    proof: null,
  });

  const [sharedProof, setSharedProof] = useState(null);
  const currentAddressFileRef = useRef(null);
  const permanentAddressFileRef = useRef(null);
  const sharedProofFileRef = useRef(null);

  const [remarks, setRemarks] = useState('');
  const [verificationStatus, setVerificationStatus] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [error, setError] = useState(null);

  // Check if current and permanent addresses are same
  const areAddressesSame = () => {
    return (
      currentAddress.line1 === permanentAddress.line1 &&
      currentAddress.line2 === permanentAddress.line2 &&
      currentAddress.country === permanentAddress.country &&
      currentAddress.state === permanentAddress.state &&
      currentAddress.city === permanentAddress.city &&
      currentAddress.pincode === permanentAddress.pincode
    );
  };

  // Trigger file inputs
  const triggerCurrentUpload = () => currentAddressFileRef.current.click();
  const triggerPermanentUpload = () => permanentAddressFileRef.current.click();
  const triggerSharedUpload = () => sharedProofFileRef.current.click();

  // Remove files
  const removeCurrentFile = () => setCurrentAddress({ ...currentAddress, proof: null });
  const removePermanentFile = () => setPermanentAddress({ ...permanentAddress, proof: null });
  const removeSharedFile = () => setSharedProof(null);

  const handleFileChange = (e, type) => {
    if (!e.target.files || !e.target.files[0]) return;
    const file = e.target.files[0];
    const allowedFileTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];

    if (!allowedFileTypes.includes(file.type)) {
      toast.error('Please upload a file in JPG, JPEG, PNG, or PDF format.');
      return;
    }

    if (type === 'current') setCurrentAddress({ ...currentAddress, proof: file });
    else if (type === 'permanent') setPermanentAddress({ ...permanentAddress, proof: file });
    else setSharedProof(file);
  };

  const getFileNameFromUrl = (url) => (url ? url.split('/').pop() : '');

  useEffect(() => {
    const fetchAddressData = async () => {
      if (!candidateId || !token) {
        setFetchingData(false);
        return;
      }
      try {
        const response = await getAddressInfo(candidateId);
        const data = response.data?.data;

        if (data) {
          setCurrentAddress({
            line1: data.currentAddressLine1 || '',
            line2: data.currentAddressLine2 || '',
            country: data.currentCountry || '',
            state: data.currentState || '',
            city: data.currentCity || '',
            pincode: data.currentPinCode || '',
            proof: data.currentAddressFileName || null,
          });

          setPermanentAddress({
            line1: data.permanentAddressLine1 || '',
            line2: data.permanentAddressLine2 || '',
            country: data.permanentCountry || '',
            state: data.permanentState || '',
            city: data.permanentCity || '',
            pincode: data.permanentPinCode || '',
            proof: data.permanentAddressFileName || null,
          });

          if (
            data.currentAddressFileName && data.permanentAddressFileName &&
            data.currentAddressFileName === data.permanentAddressFileName
            ) {
                setSharedProof(data.currentAddressFileName);
            }
          setRemarks(data.remarks || '');
          setVerificationStatus(data.verificationStatus || false);
        } else {
          applyFallback();
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch address data.');
        applyFallback();
      } finally {
        setFetchingData(false);
      }
    };

    fetchAddressData();
  }, [candidateId, token, addressInfoResponse]);

  const applyFallback = () => {
    setCurrentAddress({
      line1: addressInfoResponse?.currentAddressLine1 || '',
      line2: addressInfoResponse?.currentAddressLine2 || '',
      country: addressInfoResponse?.currentCountry || '',
      state: addressInfoResponse?.currentState || '',
      city: addressInfoResponse?.currentCity || '',
      pincode: addressInfoResponse?.currentPinCode || '',
      proof: addressInfoResponse?.currentAddressProofFileName || null,
    });

    setPermanentAddress({
      line1: addressInfoResponse?.permanentAddressLine1 || '',
      line2: addressInfoResponse?.permanentAddressLine2 || '',
      country: addressInfoResponse?.permanentCountry || '',
      state: addressInfoResponse?.permanentState || '',
      city: addressInfoResponse?.permanentCity || '',
      pincode: addressInfoResponse?.permanentPinCode || '',
      proof: addressInfoResponse?.permanentAddressProofFileName || null,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    const sharedMode = areAddressesSame();
    const hasProof =
      sharedMode
        ? sharedProof instanceof File || (typeof sharedProof === 'string' && sharedProof)
        : currentAddress.proof || permanentAddress.proof;

    if (!hasProof) {
      toast.error('At least one address proof is required.');
      setLoading(false);
      return;
    }

    if (!remarks.trim()) {
      toast.error('Remarks are required.');
      setLoading(false);
      return;
    }

    if (!candidateId) {
      setError('Candidate ID is missing. Cannot submit.');
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append('candidateId', candidateId);
    formData.append('currentAddressLine1', currentAddress.line1);
    formData.append('currentAddressLine2', currentAddress.line2);
    formData.append('currentCountry', currentAddress.country);
    formData.append('currentState', currentAddress.state);
    formData.append('currentCity', currentAddress.city);
    formData.append('currentPinCode', currentAddress.pincode);

    formData.append('permanentAddressLine1', permanentAddress.line1);
    formData.append('permanentAddressLine2', permanentAddress.line2);
    formData.append('permanentCountry', permanentAddress.country);
    formData.append('permanentState', permanentAddress.state);
    formData.append('permanentCity', permanentAddress.city);
    formData.append('permanentPinCode', permanentAddress.pincode);

    formData.append('remarks', remarks);
    formData.append('verificationStatus', JSON.stringify(verificationStatus));

    // Append proofs
    if (sharedMode && sharedProof instanceof File) {
      formData.append('currentAddressProofFile', sharedProof);
      formData.append('permanentAddressProofFile', sharedProof);
    } else {
      if (currentAddress.proof instanceof File) formData.append('currentAddressProofFile', currentAddress.proof);
      if (permanentAddress.proof instanceof File) formData.append('permanentAddressProofFile', permanentAddress.proof);
    }

    if (!token) {
      setError('Authentication token not found. Please log in.');
      setLoading(false);
      return;
    }

    try {
      await saveAddress(formData);
      toast.success('Address Check data saved successfully', { autoClose: 3000 });
      onBack();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to verify address.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const sharedMode = areAddressesSame();

  return (
    <div className="dashboard-app-container">
      <Sidebar />
      <div className="dashboard-right-panel">
        <TopNavbar />
        <div className="address-info-header">
          <h2>Address Check</h2>
        </div>
        <div className="address-info-container">
          <form onSubmit={handleSubmit} className="address-info-form">
            {/* Current Address Section */}
            <h3>Current Address Details</h3>
            <div className="address-section">
              <div className="form-grid">
                <div className="form-group">
                  <label><strong>Address Line 1:</strong></label>
                  <textarea value={currentAddress.line1} disabled rows="3" />
                </div>
                <div className="form-group">
                  <label><strong>Address Line 2:</strong></label>
                  <textarea value={currentAddress.line2} disabled rows="3" />
                </div>
                <div className="form-group">
                  <label><strong>Country:</strong></label>
                  <input type="text" value={currentAddress.country} disabled />
                </div>
                <div className="form-group">
                  <label><strong>State:</strong></label>
                  <input type="text" value={currentAddress.state} disabled />
                </div>
                <div className="form-group">
                  <label><strong>City:</strong></label>
                  <input type="text" value={currentAddress.city} disabled />
                </div>
                <div className="form-group">
                  <label><strong>PinCode:</strong></label>
                  <input type="text" value={currentAddress.pincode} disabled />
                </div>
              </div>

              {/* Current Address Proof */}
              {!sharedMode && (
                <div className="form-group Employee-address-file-upload-group">
                  <label><strong>Address Proof:</strong></label>
                  <input type="file" ref={currentAddressFileRef} className="Employee-address-file-input" accept="image/jpeg,image/png,application/pdf" onChange={(e) => handleFileChange(e, 'current')} />
                  <div className="Employee-address-upload-certificate-button" onClick={triggerCurrentUpload}>Drag & Drop or Click to Upload</div>
                  {currentAddress.proof && (
                    <div className="file-list">
                      <span className="file-list-item">
                        {currentAddress.proof instanceof File ? currentAddress.proof.name : getFileNameFromUrl(currentAddress.proof)}
                        <FaTimes className="remove-file-icon" onClick={removeCurrentFile} />
                      </span>
                    </div>
                  )}
                  <p className="Employee-address-upload-certificate-note">* File must be less than 5MB and must be PDF / JPG / PNG.</p>
                </div>
              )}
            </div>

            {/* Permanent Address Section */}
            <h3>Permanent Address Details</h3>
            <div className="address-section">
              <div className="form-grid">
                <div className="form-group">
                  <label><strong>Address Line 1:</strong></label>
                  <textarea value={permanentAddress.line1} disabled rows="3" />
                </div>
                <div className="form-group">
                  <label><strong>Address Line 2:</strong></label>
                  <textarea value={permanentAddress.line2} disabled rows="3" />
                </div>
                <div className="form-group">
                  <label><strong>Country:</strong></label>
                  <input type="text" value={permanentAddress.country} disabled />
                </div>
                <div className="form-group">
                  <label><strong>State:</strong></label>
                  <input type="text" value={permanentAddress.state} disabled />
                </div>
                <div className="form-group">
                  <label><strong>City:</strong></label>
                  <input type="text" value={permanentAddress.city} disabled />
                </div>
                <div className="form-group">
                  <label><strong>PinCode:</strong></label>
                  <input type="text" value={permanentAddress.pincode} disabled />
                </div>

                {/* Permanent Address Proof */}
                {!sharedMode && (
                  <div className="form-group Employee-address-file-upload-group">
                    <label><strong>Address Proof:</strong></label>
                    <input type="file" ref={permanentAddressFileRef} className="Employee-address-file-input" accept="image/jpeg,image/png,application/pdf" onChange={(e) => handleFileChange(e, 'permanent')} />
                    <div className="Employee-address-upload-certificate-button" onClick={triggerPermanentUpload}>Drag & Drop or Click to Upload</div>
                    {permanentAddress.proof && (
                      <div className="file-list">
                        <span className="file-list-item">
                          {permanentAddress.proof instanceof File ? permanentAddress.proof.name : getFileNameFromUrl(permanentAddress.proof)}
                          <FaTimes className="remove-file-icon" onClick={removePermanentFile} />
                        </span>
                      </div>
                    )}
                    <p className="Employee-address-upload-certificate-note">* File must be less than 5MB and must be PDF / JPG / PNG.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Shared Proof Section */}
            {sharedMode && (
              <div className="form-group Employee-address-file-upload-group">
                <label><strong>Shared Address Proof (Current & Permanent):</strong></label>
                <input type="file" ref={sharedProofFileRef} className="Employee-address-file-input" accept="image/jpeg,image/png,application/pdf" onChange={(e) => handleFileChange(e, 'shared')} />
                <div className="Employee-address-upload-certificate-button" onClick={triggerSharedUpload}>Drag & Drop or Click to Upload</div>
                {sharedProof && (
                  <div className="file-list">
                    <span className="file-list-item">
                      {sharedProof instanceof File ? sharedProof.name : getFileNameFromUrl(sharedProof)}
                      <FaTimes className="remove-file-icon" onClick={removeSharedFile} />
                    </span>
                  </div>
                )}
                <p className="Employee-address-upload-certificate-note">* File must be less than 5MB and must be PDF / JPG / PNG.</p>
              </div>
            )}

            {/* Remarks */}
            <div className="remarks-section">
              <label><strong>Remarks:</strong></label>
              <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Type here..." />
            </div>

            {/* Verification */}
            <div className="checkbox-group">
              <label>
                <input type="checkbox" checked={verificationStatus} onChange={() => setVerificationStatus(!verificationStatus)} />
                Verification Status
              </label>
              <p className="verification-note">
                ⚠️ Once verified, this step will be locked and you cannot return to modify the address details.
              </p>
            </div>

            <div className="form-actions">
              <button type="button" className="cancel-button" onClick={onBack} disabled={loading}>Cancel</button>
              <button type="submit" className="submit-button" disabled={loading}>{loading ? 'Submitting...' : 'Submit'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

AddressInfo.propTypes = {
  candidateId: PropTypes.object,
  addressInfoResponse: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default AddressInfo;
